import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useLayoutEffect, useState } from 'react';
import Header from '@/components/ui/Header';
import { cn } from '@/lib/utils';
import { setProfessionalData } from '@/utils/cookies';
import { useGetMe } from '@/services';
import {
	isSessionCallAccessState,
	isSessionEligibleForCallProntuario,
	type SessionCallLayoutOutletContext,
	type VideoCallHeaderConfig,
} from '@/constants/sessionCall';
import { PRONTUARIO_BG_CLASS } from '@/pages/professional/sessao-chamada/constants/prontuarioTheme';
import { TUTORIAL_DEMONSTRACAO_BACKDROP_BG } from '@/constants/professionalTutorialDemo';

const ProfessionalSessionCallLayout = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [videoCallHeaderConfig, setVideoCallHeaderConfig] =
		useState<VideoCallHeaderConfig | null>(null);
	const { data, isLoading } = useGetMe();

	useLayoutEffect(() => {
		if (isLoading) return;

		if (!data) {
			navigate('/profissional/login', { replace: true });
			return;
		}

		setProfessionalData(data);

		if (!data.authorized) {
			navigate('/profissional/autorizacao', { replace: true });
		}
	}, [data, isLoading, navigate]);

	const accessState = isSessionCallAccessState(location.state)
		? location.state
		: null;
	const isProfessionalCall = accessState?.viewerRole === 'professional';
	const isProntuarioSessionCall =
		isProfessionalCall &&
		isSessionEligibleForCallProntuario({
			sessionType: accessState?.sessionType,
			isOnline: accessState?.isOnline ?? true,
			transcriptionAllowed:
				accessState?.sessionType === 'P2P' ||
				accessState?.sessionType === 'P2P_TRANSCRIPTION_ONLY' ||
				accessState?.transcriptionAllowed === true,
			transcriptionMock: accessState?.transcriptionMock === true,
		});
	const isTutorialDemonstracao = location.pathname.includes(
		'/profissional/tutorial/demonstracao',
	);

	const companyBannerOverride = isProfessionalCall
		? accessState.companyBanner
		: undefined;

	useLayoutEffect(() => {
		if (!isProntuarioSessionCall) return;

		setVideoCallHeaderConfig((current) => ({
			onBack: current?.onBack ?? (() => navigate('/profissional/home')),
			onLogoClick: current?.onLogoClick,
			onToggleLayout: current?.onToggleLayout ?? (() => undefined),
			activeSpeakerMode: current?.activeSpeakerMode ?? true,
			controlsEnabled: current?.controlsEnabled ?? false,
			showLayoutToggle: current?.showLayoutToggle ?? false,
			hideBackButton: current?.hideBackButton,
			meetingScheduledStartMs: current?.meetingScheduledStartMs ?? null,
			hideMeetingTimer: current?.hideMeetingTimer,
		}));
	}, [isProntuarioSessionCall, navigate]);

	if (!data) return null;

	return (
		<section
			className={cn(
				'relative flex min-h-screen w-full flex-col overflow-x-hidden',
				isProntuarioSessionCall &&
					!isTutorialDemonstracao &&
					PRONTUARIO_BG_CLASS,
				!isProntuarioSessionCall &&
					!isTutorialDemonstracao &&
					'bg-slate-900',
			)}
			style={
				isTutorialDemonstracao
					? { backgroundColor: TUTORIAL_DEMONSTRACAO_BACKDROP_BG }
					: undefined
			}
		>
			<Header
				showCompanyBanner={false}
				videoCallMode
				videoCallHeaderConfig={videoCallHeaderConfig}
				companyBannerOverride={companyBannerOverride}
			/>
			<main className="relative flex h-[calc(100vh-64px)] min-h-0 w-full">
				<section className="relative flex h-full min-h-0 w-full flex-1 justify-stretch overflow-hidden">
					<Outlet
						context={
							{
								setVideoCallHeaderConfig,
							} satisfies SessionCallLayoutOutletContext
						}
					/>
				</section>
			</main>
		</section>
	);
};

export { ProfessionalSessionCallLayout };
